import { HubConnectionState } from "@microsoft/signalr";
import { loadWrapper } from "aic";
import { useUnsavedChanges } from "contexts/UnsavedChangesContextProvider";
import { CommentReadDto } from "functions/api/comments/interface";
import { eHierarchy, eHierarchyLevel, eTlfItemType } from "functions/api/enums";
import { HierarchyQueryParams } from "functions/api/hierarchy/HierarchyQueryParams";
import { HierarchyReadDto } from "functions/api/hierarchy/HierarchyReadDto";
import { restoreBackupReport } from "functions/api/hierarchy/restoreBackupReport";
import {
  HierarchySaveDto,
  HierarchyUpdateDto,
  saveHierarchyData,
} from "functions/api/hierarchy/saveHierarchyData";
import {
  SaveTocAsMajorVersionParams,
  saveTocAsMajorVersion,
} from "functions/api/hierarchy/saveTocAsMajorVersion";
import { IRequestStatus } from "functions/api/interface";
import { ClinicalLibrariesReadDtoReturn } from "functions/api/library/getLibraries";
import { TocItemsUpdateProps, updateTocItems } from "functions/api/toc/updateTocItems";
import { useDocumentTitle } from "functions/useDocumentTitle";
import { isEqual } from "lodash";
import {
  useCallback,
  useContext,
  useEffect,
  useEffectEvent,
  useRef,
  useState,
  useTransition,
} from "react";
import { Patch } from "rfc6902";
import { AppSettingsContext } from "../AppSettingsContext";
import { HubGroupMessage, TocActionSignal } from "../signalr/interface";
import { SignalRContext } from "../SignalRProvider";
import {
  MosaicTocEntryDto,
  MosaicTocSectionDto,
  SAVE_FAIL,
  SAVE_SUCCESS,
  SET_TOC_REQUEST_STATUS,
  SET_TYPE_FILTER,
  SHOW_TOC_NEED_REFRESH,
  SHOW_TOC_SAVE_ALERT,
  TocUpdateMessage,
} from "../toc/interface";
import { processActionFromSignal } from "../toc/processActionFromSignal";
import { refreshToc } from "../toc/refreshToc";
import { loadTocTlfItemFilterFromStorage } from "../toc/tocTlfItemFilter";
import { getHierarchyBaseUrl } from "./getHierarchyBaseUrl";
import {
  PATCH_HIERARCHY_COMMENTS,
  REMOVE_HIERARCHY_COMMENT,
  SAVE_HIERARCHY_COMPLETED,
  SAVE_HIERARCHY_FAILED,
  SET_HIERARCHY_ID,
  SET_HIERARCHY_SAVE_REQUEST_STATUS,
  SET_HIERARCHY_SAVE_TOC_REQUEST_STATUS,
  UPDATE_HIERARCHY,
  UPDATE_HIERARCHY_COMMENT,
} from "./interface";
import { useClinicalLibraryLoaderEffect } from "./useClinicalLibraryLoaderEffect";

interface UseHierarchyEffectsProps {
  clinicalLibraryPromise?: Promise<ClinicalLibrariesReadDtoReturn>;
}

/**
 * Hook holding all hierarchy side effects (saving, ToC major version saving, clinical library
 * lazy-loading, SignalR group membership/listeners, document title and unsaved-changes
 * registration) driven by the unified {@link AppSettingsContext} hierarchy slice.
 * Consumed by {@link HierarchyContextProvider}, which remains the render position for these
 * effects so that they stay scoped to hierarchy/ToC routes only.
 *
 * @param props - Props for the hook
 * @param props.clinicalLibraryPromise - Optional promise for the deferred Clinical Library request
 * @returns The hierarchy title, save request statuses, and a setter for the current base URL
 *          used to detect hierarchy/report navigation after a save.
 */
export const useHierarchyEffects = ({ clinicalLibraryPromise }: UseHierarchyEffectsProps) => {
  const { state: appSettings, dispatch } = useContext(AppSettingsContext);
  const state = appSettings.hierarchyState;
  const signalR = useContext(SignalRContext);
  const { navigateAfterSave, registerUnsavedChanges } = useUnsavedChanges(
    "hierarchy-context-provider",
  );

  useClinicalLibraryLoaderEffect({
    clinicalLibraryPromise,
    dispatch,
  });

  const saveRequestStatus = state.saveRequestStatus;
  const saveTocRequestStatus = state.saveTocRequestStatus;
  const setSaveRequestStatus = useCallback(
    (status: IRequestStatus<HierarchySaveDto>) =>
      dispatch({ operation: SET_HIERARCHY_SAVE_REQUEST_STATUS, saveRequestStatus: status }),
    [dispatch],
  );
  const setSaveTocRequestStatus = useCallback(
    (status: IRequestStatus<SaveTocAsMajorVersionParams>) =>
      dispatch({ operation: SET_HIERARCHY_SAVE_TOC_REQUEST_STATUS, saveTocRequestStatus: status }),
    [dispatch],
  );
  const [, startTransition] = useTransition();

  const tocState = appSettings.tocState;
  const [tocSaveRequestStatus, setTocSaveRequestStatus] = useState<
    IRequestStatus<TocItemsUpdateProps>
  >({
    requesting: false,
    error: false,
  });
  const setTocRequestStatus = useCallback(
    (status: IRequestStatus<HierarchyQueryParams>) =>
      dispatch({ operation: SET_TOC_REQUEST_STATUS, tocRequestStatus: status }),
    [dispatch],
  );
  // Messages already processed or sent by this client, used to de-duplicate SignalR echoes
  const [receivedMessages, setReceivedMessages] = useState<TocUpdateMessage[]>([]);

  const currentBaseUrlRef = useRef<string>("");

  /**
   * Update the tracked current base URL used to detect hierarchy/report navigation after a save.
   * Called by {@link HierarchyContextProvider} when new loader data is applied.
   *
   * @param baseUrl - The base URL of the hierarchy currently loaded into context
   */
  const setCurrentBaseUrl = useCallback((baseUrl: string) => {
    currentBaseUrlRef.current = baseUrl;
  }, []);

  /**
   * Send a SignalR update to the group when the hierarchyId is changed because of a save major version
   * @param oldHierarchyId Old hierarchy ID
   * @param newHierarchy New hierarchy data
   */
  const sendSignalRHierarchyUpdate = useCallback(
    async (oldHierarchyId: string, newHierarchy: HierarchyReadDto) => {
      if (signalR && signalR.connection?.state === HubConnectionState.Connected) {
        signalR.sendTocSaveToGroup({ oldHierarchyId, newHierarchy }, `toc${oldHierarchyId}`);
      }
    },
    [signalR],
  );

  /**
   * Save the current hierarchy state
   * Does not send a SignalR update
   */
  const saveHierarchy = useEffectEvent(async () => {
    /* istanbul ignore else */
    if (state.hierarchy) {
      const updateDto: HierarchyUpdateDto = {
        id: state.hierarchy.id,
        name: state.hierarchy.name,
        parentTag: state.hierarchy.parentTag,
        description: state.hierarchy.description,
        lists: state.hierarchy.lists,
        phase: state.hierarchy.phase,
        reportType: state.hierarchy.reportType,
        rowGroups: state.hierarchy.rowGroups,
        sections: state.hierarchy.sections,
        singles: state.hierarchy.singles,
        status: state.hierarchy.status,
        domainList: state.hierarchy.domainList,
        domainReplacements: state.hierarchy.domainReplacements,
        arsEnabled: state.hierarchy.arsEnabled,
        analysisMethods: state.hierarchy.analysisMethods,
        analysisSets: state.hierarchy.analysisSets,
        dataSubsets: state.hierarchy.dataSubsets,
        groupingFactors: state.hierarchy.groupingFactors,
        tlfTags: state.hierarchy.tlfTags,
      };
      const response =
        state.hierarchy.isBackupVersion && state.hierarchy.type === eHierarchy.Report
          ? await loadWrapper(
              restoreBackupReport,
              updateDto,
              { ...saveRequestStatus, error: false, requestedId: undefined, errorText: undefined },
              setSaveRequestStatus,
              appSettings.backEndUrl,
            )
          : await loadWrapper(
              saveHierarchyData,
              updateDto,
              {
                ...saveRequestStatus,
                error: false,
                requestedId: undefined,
                errorText: undefined,
              },
              setSaveRequestStatus,
              appSettings.backEndUrl,
            );
      /* istanbul ignore else */
      if (response?.success) {
        dispatch({
          operation: SAVE_HIERARCHY_COMPLETED,
          hierarchy: {
            ...response.data,
            clinicalLibrary: state.hierarchy.clinicalLibrary,
          },
        });
        // istanbul ignore else
        if (!state.noRedirect) {
          const newBaseUrl = getHierarchyBaseUrl(response.data);
          // When restoring a back up, this should trigger
          if (state.hierarchy.originHierarchyId)
            await sendSignalRHierarchyUpdate(state.hierarchy.originHierarchyId, response.data);
          if (response.data.id !== state.hierarchy.id || newBaseUrl !== currentBaseUrlRef.current) {
            currentBaseUrlRef.current = newBaseUrl;
            navigateAfterSave(newBaseUrl);
          }
        }
      } else if (response?.success === false) {
        console.warn(`Save failed for hierarchy: ${saveRequestStatus.errorText}`);
        dispatch({ operation: SAVE_HIERARCHY_FAILED });
      }
    }
  });

  useEffect(() => {
    if (state.doSave && !saveRequestStatus.requesting) {
      saveHierarchy();
    }
  }, [saveRequestStatus.requesting, state.doSave]);

  /**
   * Save the current hierarchy as a new major version
   * This will send a SignalR update to the group to indicate that the hierarchyId has changed
   * @param saveTocDescription Description for the save
   */
  const saveTocMajor = useEffectEvent(async (saveTocDescription: string) => {
    /* istanbul ignore else */
    if (state.hierarchy) {
      const updateDto: HierarchyUpdateDto = {
        id: state.hierarchy.id,
        name: state.hierarchy.name,
        parentTag: state.hierarchy.parentTag,
        description: state.hierarchy.description,
        lists: state.hierarchy.lists,
        phase: state.hierarchy.phase,
        reportType: state.hierarchy.reportType,
        rowGroups: state.hierarchy.rowGroups,
        sections: state.hierarchy.sections,
        singles: state.hierarchy.singles,
        status: state.hierarchy.status,
        domainList: state.hierarchy.domainList,
        domainReplacements: state.hierarchy.domainReplacements,
        arsEnabled: state.hierarchy.arsEnabled,
        analysisMethods: state.hierarchy.analysisMethods,
        analysisSets: state.hierarchy.analysisSets,
        dataSubsets: state.hierarchy.dataSubsets,
        groupingFactors: state.hierarchy.groupingFactors,
        tlfTags: state.hierarchy.tlfTags,
      };

      const response = await loadWrapper(
        saveTocAsMajorVersion,
        {
          hierarchy: updateDto,
          hierarchyId: updateDto.id,
          parentTag: updateDto.parentTag,
          saveTocDescription,
        },
        {
          ...saveTocRequestStatus,
          error: false,
          requestedId: undefined,
          errorText: undefined,
        },
        setSaveTocRequestStatus,
        appSettings.backEndUrl,
      );
      /* istanbul ignore else */
      if (response?.success) {
        dispatch({
          operation: SAVE_HIERARCHY_COMPLETED,
          hierarchy: { ...response.data, clinicalLibrary: state.hierarchy.clinicalLibrary },
          oldHierarchyId: state.hierarchy.id,
        });
        // Always send signalR update
        await sendSignalRHierarchyUpdate(state.hierarchy.id, response.data);
        /* istanbul ignore else */
        if (response.data.id !== state.hierarchy.id) {
          const newBaseUrl = getHierarchyBaseUrl(response.data);
          currentBaseUrlRef.current = newBaseUrl;
          navigateAfterSave(newBaseUrl);
        }
      } else if (response?.success === false) {
        console.warn(`Save failed for hierarchy: ${saveRequestStatus.errorText}`);
        dispatch({ operation: SAVE_HIERARCHY_FAILED });
      }
    }
  });

  /**
   * Effect to save the ToC as a major version when the state.doTocSave is true
   */
  useEffect(() => {
    if (state.doTocSave && !saveTocRequestStatus.requesting && state.saveTocDescription) {
      saveTocMajor(state.saveTocDescription);
    }
  }, [saveTocRequestStatus.requesting, state.doTocSave, state.saveTocDescription]);

  /**
   * Join signalR group for the hierarchy
   * This is done to receive comment updates for the hierarchy
   */
  const [sendGroupRequest, setSendGroupRequest] = useState<string>("❄️");
  useEffect(() => {
    if (
      signalR &&
      signalR.connection?.state === HubConnectionState.Connected &&
      state.hierarchy &&
      sendGroupRequest !== `hie${state.hierarchy.id}`
    ) {
      signalR.connectToGroup(`hie${state.hierarchy.id}`);
      startTransition(() => {
        setSendGroupRequest(`hie${state.hierarchy!.id}`);
      });
    }
  }, [sendGroupRequest, signalR, signalR?.connection?.state, state.hierarchy]);

  // ToC SignalR group, based on the current hierarchy
  const tocSignalRGroup = state.hierarchy ? `toc${state.hierarchy.id}` : null;

  /**
   * Load ToC view mode filter from localStorage on initialisation
   */
  useEffect(() => {
    const savedFilter = loadTocTlfItemFilterFromStorage();
    if (!isEqual(savedFilter, tocState.showTypes)) {
      dispatch({
        operation: SET_TYPE_FILTER,
        showTypes: savedFilter,
      });
    }
  }, [dispatch, tocState.showTypes]);

  /**
   * Join signalR group for the ToC
   * This is done to receive tocSave and tocUpdate messages
   */
  const [tocSendGroupRequest, setTocSendGroupRequest] = useState<string>("❄️");
  useEffect(() => {
    if (
      signalR &&
      signalR.connection?.state === HubConnectionState.Connected &&
      tocSignalRGroup &&
      tocSendGroupRequest !== tocSignalRGroup
    ) {
      signalR.connectToGroup(tocSignalRGroup);
      startTransition(() => {
        setTocSendGroupRequest(tocSignalRGroup);
      });
    }
  }, [tocSendGroupRequest, signalR, tocSignalRGroup, signalR?.connection?.state]);

  /**
   * Save ToC items provider function
   * @param tocItemsToUpdate - The items to update
   */
  const saveTocItems = useEffectEvent(async (tocItemsToUpdate: TocItemsUpdateProps) => {
    const changeList = tocState.tocItems.filter((i) => i.changed).map((i) => i.id);
    const response = await loadWrapper(
      updateTocItems,
      tocItemsToUpdate,
      { ...tocSaveRequestStatus, requestedId: undefined },
      setTocSaveRequestStatus,
      appSettings.backEndUrl,
    );
    /* istanbul ignore else */
    if (response?.success && state.hierarchy) {
      const newHierarchy = {
        ...state.hierarchy,
        tocMajorVersion: response.data.tocMajorVersion,
        tocMinorVersion: response.data.tocMinorVersion,
        sections: response.data.sections,
      };
      dispatch({
        operation: UPDATE_HIERARCHY,
        hierarchy: newHierarchy,
      });
      dispatch({
        operation: SAVE_SUCCESS,
        itemsUpdate: tocItemsToUpdate,
        saveTimeStamp: Date.now(),
      });
      /* istanbul ignore else */
      if (tocSignalRGroup && signalR) {
        const signalRUpdate: TocActionSignal = {
          hierarchy: newHierarchy,
          items: [
            ...(response.data.sections
              .filter((i) => changeList.includes(i.id))
              .map((s) => ({
                id: s.id,
                primaryHierarchyId: newHierarchy.id,
                tocNumber: s.sectionNumber,
                tocLabel: s.sectionLabel,
                type: eTlfItemType.section,
                tocOrder: s.order,
                changed: false,
                inError: false,
              })) as MosaicTocSectionDto[]),
            ...(response.data.shells
              .filter((i) => changeList.includes(i.id))
              .map((i) => ({
                ...i,
                changed: false,
                inError: false,
              })) as MosaicTocEntryDto[]),
          ],
          deletedItemId: tocItemsToUpdate.deletedItemId,
        };
        const sentMessage: HubGroupMessage = await signalR.sendTocUpdateToGroup(
          signalRUpdate,
          tocSignalRGroup,
        );
        const newTlfMessage: TocUpdateMessage = {
          messageId: sentMessage.messageId,
          updatedIds: ["sentMessage"],
          updateTS: new Date(sentMessage.timeStamp),
        };
        setReceivedMessages([...receivedMessages, newTlfMessage]);
      }
    } else if (response?.success === false) {
      console.warn("Save failed", response?.errorText);
      dispatch({
        operation: SAVE_FAIL,
      });
    }
  });

  /**
   * Save effect for ToC items
   */
  useEffect(() => {
    if (tocState.saving && !tocSaveRequestStatus.requesting && state.hierarchy) {
      const tocItemsToUpdate: TocItemsUpdateProps = {
        hierarchyId: tocState.hierarchyId,
        items: tocState.tocItems
          .filter((i) => i.changed)
          .map((n) => ({
            tocId: n.id,
            tocNumber:
              n.tocNumber ??
              // istanbul ignore next
              "",
            tocLabel:
              n.tocLabel ??
              // istanbul ignore next
              "",
            tlfType: n.type,
            order:
              n.tocOrder ??
              // istanbul ignore next
              0,
          })),
        deletedItemId: tocState.removedItems?.findLast((i) => i.changed)?.id,
      };
      startTransition(() => {
        saveTocItems(tocItemsToUpdate);
      });
    }
  }, [
    state.hierarchy,
    tocSaveRequestStatus.requesting,
    tocState.hierarchyId,
    tocState.removedItems,
    tocState.saving,
    tocState.tocItems,
  ]);

  /**
   * Send update to get the current hierarchy version
   */
  useEffect(() => {
    if (
      tocState.blocked === "getNewVersion" &&
      !tocSaveRequestStatus.requesting &&
      state.hierarchy
    ) {
      const tocItemsToUpdate: TocItemsUpdateProps = {
        hierarchyId: tocState.hierarchyId,
        items: [],
      };
      startTransition(() => {
        saveTocItems(tocItemsToUpdate);
      });
    }
  }, [state.hierarchy, tocSaveRequestStatus.requesting, tocState.blocked, tocState.hierarchyId]);

  /**
   * Initial load of ToC items
   */
  useEffect(() => {
    if (state.hierarchy && state.hierarchy.level === eHierarchyLevel.Report) {
      // Check if there are zero items loaded
      if (tocState.tocItems.length === 0 || tocState.hierarchyId !== state.hierarchy.id) {
        refreshToc(
          tocState.tocRequestStatus,
          setTocRequestStatus,
          appSettings.backEndUrl,
          state.hierarchy,
          dispatch,
          false,
        );
      }
    }
  }, [
    appSettings.backEndUrl,
    dispatch,
    setTocRequestStatus,
    state.hierarchy,
    tocState.hierarchyId,
    tocState.tocItems.length,
    tocState.tocRequestStatus,
  ]);

  /**
   * Create the combined hierarchy/ToC signalR group listener
   */
  useEffect(() => {
    /**
     * Handle incoming SignalR group messages for both hierarchy and ToC updates.
     *
     * @param message - Raw JSON string containing the group message payload.
     */
    const processSignalRMessage = (message: string) => {
      const groupUpdate = JSON.parse(message) as HubGroupMessage;

      // Hierarchy comment updates
      if (
        groupUpdate.groupName === `hie${state.hierarchy?.id}` &&
        groupUpdate.displayName !== signalR?.connection?.connectionId &&
        groupUpdate.displayName !== "Backend" &&
        groupUpdate.text &&
        state.hierarchy?.comments
      ) {
        switch (groupUpdate.messageType) {
          case "deleteComment": {
            try {
              const hieDelCommentId = JSON.parse(groupUpdate.text[0]).id;
              /* istanbul ignore else */
              if (hieDelCommentId) {
                dispatch({
                  operation: REMOVE_HIERARCHY_COMMENT,
                  commentId: hieDelCommentId,
                });
              }
            } catch (error) {
              // istanbul ignore next
              console.warn("Error processing delete comment update:", error);
            }
            break;
          }
          case "comment": {
            try {
              const hieUpdateMessage = JSON.parse(groupUpdate.text[0]) as CommentReadDto;
              /* istanbul ignore else */
              if (hieUpdateMessage) {
                dispatch({
                  operation: UPDATE_HIERARCHY_COMMENT,
                  comment: hieUpdateMessage,
                });
              }
            } catch (error) {
              // istanbul ignore next
              console.warn("Error processing comment update:", error);
            }
            break;
          }
          case "commentsPatch": {
            try {
              const commentsPatch = JSON.parse(groupUpdate.text[0]) as Patch;
              /* istanbul ignore else */
              if (commentsPatch) {
                dispatch({
                  operation: PATCH_HIERARCHY_COMMENTS,
                  patch: commentsPatch,
                });
              }
            } catch (error) {
              // istanbul ignore next
              console.warn("Error processing comments patch update:", error);
            }
            break;
          }
        }
      }

      // ToC updates
      const tocItemsToUpdate =
        groupUpdate.text && groupUpdate.text[0].startsWith("{")
          ? (JSON.parse(groupUpdate.text[0]) as TocActionSignal)
          : undefined;
      if (
        groupUpdate.messageType === "tooBig" &&
        groupUpdate.displayName !== signalR?.connection?.connectionId
      ) {
        dispatch({ operation: SHOW_TOC_NEED_REFRESH });
        return;
      }
      if (
        state.hierarchy &&
        groupUpdate.displayName !== signalR?.connection?.connectionId &&
        groupUpdate.displayName !== "Backend" &&
        ["tocUpdate", "tocSave"].includes(groupUpdate.messageType) &&
        tocItemsToUpdate &&
        !receivedMessages.find((r) => r.messageId === groupUpdate.messageId)
      ) {
        // Update processing
        const newAction = processActionFromSignal(groupUpdate.messageId, tocItemsToUpdate);
        setReceivedMessages([...receivedMessages, newAction.receivedMessage]);
        /* istanbul ignore else */
        if (
          tocItemsToUpdate.hierarchy.id === state.hierarchy.id &&
          groupUpdate.messageType === "tocUpdate"
        ) {
          dispatch(newAction);
          dispatch({ operation: SAVE_SUCCESS, saveTimeStamp: Date.now() });
          dispatch({
            operation: SAVE_HIERARCHY_COMPLETED,
            hierarchy: tocItemsToUpdate.hierarchy,
          });
        } else if (
          tocItemsToUpdate.hierarchy.id !== state.hierarchy.id &&
          groupUpdate.messageType === "tocSave"
        ) {
          dispatch({
            operation: SHOW_TOC_SAVE_ALERT,
            tocIdFromMessage: tocItemsToUpdate.hierarchy.id,
          });
          dispatch({
            operation: SET_HIERARCHY_ID,
            hierarchyId: tocItemsToUpdate.hierarchy.id,
          });
        }
      }
    };
    signalR?.connection?.on("SendMessageToGroup", processSignalRMessage);
    // return () => {
    //   signalR?.connection?.off("SendMessageToGroup", processSignalRMessage);
    // };
  }, [dispatch, receivedMessages, signalR?.connection, state.hierarchy]);

  /**
   * Page title based on hierarchy state
   */
  const title = state.hierarchy ? `${state.hierarchy.name}` : `Loading hierarchy... ${state.id}`;
  useDocumentTitle(title);

  /**
   * Effect to register unsaved changes when the hierarchy state changes
   */
  useEffect(() => {
    registerUnsavedChanges("Hierarchy", state.changed);
  }, [registerUnsavedChanges, state.changed]);

  return { title, saveRequestStatus, saveTocRequestStatus, setCurrentBaseUrl };
};
